import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { KpiCard } from "@/components/KpiCard";
import { MultiSeriesChart } from "@/components/MultiSeriesChart";
import { formatDelta, formatPct } from "@/lib/utils";
import { ConversationDaySection } from "@/pages/dashboard-sections/ConversationDaySection";

type Period = { periodStart: string; periodEnd: string };

const SERIES = [
  { key: "conversations", name: "Conversas", color: "hsl(var(--chart-1))" },
  { key: "newContacts", name: "Novos contatos", color: "hsl(var(--chart-4))" },
  { key: "answered", name: "Conversas respondidas", color: "hsl(var(--chart-3))" },
];

export function ConversationsTab({ period }: { period: Period }) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const kpis = useQuery({ queryKey: ["conversation-kpis", period], queryFn: () => client.getConversationKpis(period) });
  const daily = useQuery({ queryKey: ["conversation-daily", period], queryFn: () => client.getConversationDaily(period) });

  const data = kpis.data;

  const handleChartClick = (payload: Record<string, unknown>) => {
    const date = payload?.date;
    if (typeof date === "string") setSelectedDay(date);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-5">
        <KpiCard
          label="Conversas totais"
          value={data ? data.total.toLocaleString("pt-BR") : "—"}
          delta={data ? formatDelta(data.totalDeltaPct) : undefined}
          deltaDir={data && data.totalDeltaPct >= 0 ? "up" : "down"}
          caption="volume iniciado no período"
          accent="hsl(var(--chart-1))"
        />
        <KpiCard
          label="Novos contatos"
          value={data ? data.newContacts.toLocaleString("pt-BR") : "—"}
          delta={data ? formatDelta(data.newContactsDeltaPct) : undefined}
          deltaDir={data && data.newContactsDeltaPct >= 0 ? "up" : "down"}
          caption="leads de primeira interação"
          accent="hsl(var(--chart-4))"
        />
        <KpiCard
          label="Conversas respondidas"
          value={data ? data.answered.toLocaleString("pt-BR") : "—"}
          delta={data ? formatDelta(data.answeredDeltaPct) : undefined}
          deltaDir={data && data.answeredDeltaPct >= 0 ? "up" : "down"}
          caption={data ? `${formatPct(data.answerRate, 1)} taxa de resposta` : "—"}
          accent="hsl(var(--chart-3))"
        />
        <KpiCard
          label="Tempo médio de resposta"
          value={data ? `${data.avgResponseMinutes.toFixed(1).replace(".", ",")} min` : "—"}
          delta={data ? formatDelta(data.avgResponseDeltaPct) : undefined}
          deltaDir={data && data.avgResponseDeltaPct <= 0 ? "up" : "down"}
          caption="queda é melhor"
          accent="hsl(var(--semantic-warning))"
        />
      </div>

      <MultiSeriesChart
        data={daily.data ?? []}
        series={SERIES}
        title="Visão geral"
        subtitle="Conversas iniciadas, novos contatos e respondidas · clique num dia para ver a listagem completa"
        defaultMode="line"
        allowModeToggle={false}
        onPointClick={handleChartClick}
        pulseAllSeries
      />

      <ConversationDaySection date={selectedDay} onClear={() => setSelectedDay(null)} />
    </div>
  );
}
